import {
  JSONLoader,
  TextureLoader,
  ObjectLoader,
  RepeatWrapping,
  AudioLoader,
} from 'three'

/**
  ```
  import preloader from './utils/preloader';
  var manifest = [
    {type: 'Texture', url: './assets/images/diffuse.jpg', id: 'diffuseTexture'},
    {type: 'JsonModel', url: './assets/models/spaceship.json', id: 'spaceshipModel'}
  ];
  preloader.load(manifest, () => {
    const texture = preloader.getTexture('diffuseTexture');
    const geometry = preloader.getGeometry('spaceshipModel');
    const model = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({map: texture}));
  })
  ```
*/

class Preloader {
  constructor() {
    this.jsonLoader = new JSONLoader()
    this.objectLoader = new ObjectLoader()
    this.textureLoader = new TextureLoader()
    this.audioLoader = new AudioLoader()
    this.isLoaded = false
  }

  load(manifest, onComplete) {
    if (!manifest || !manifest.length) return
    this.manifest = manifest
    this.start(onComplete)
  }

  start(onComplete) {
    this.getManifestByType('Texture').forEach((value) => {
      value.isLoaded = false
      this.textureLoader.load(
        value.url,
        (texture) => {
          value.isLoaded = true
          value.texture = texture
          if (this.checkManifestCompletion()) {
            onComplete()
          }
        },
        undefined,
        (err) => {
          console.error('error preloading texture: ' + value.url, err)
        }
      )
    })

    this.getManifestByType('Audio').forEach((value) => {
      value.isLoaded = false
      this.audioLoader.load(
        value.url,
        (buffer) => {
          value.isLoaded = true
          value.buffer = buffer
          if (this.checkManifestCompletion()) {
            onComplete()
          }
        },
        undefined,
        (err) => {
          console.error('error preloading texture: ' + value.url, err)
        }
      )
    })

    this.getManifestByType('Image').forEach((value) => {
      value.isLoaded = false
      const img = new Image()
      img.addEventListener('load', () => {
        value.isLoaded = true
        value.image = img
        if (this.checkManifestCompletion()) {
          onComplete()
        }
      })
      img.src = value.url
    })
  }

  getManifestByType(type) {
    return this.manifest.filter(value => value.type === type)
  }

  checkManifestCompletion() {
    for (let i = 0, l = this.manifest.length; i < l; i++) {
      if (!this.manifest[i].isLoaded) return false
    }
    return true
  }

  getGeometry(id) {
    var item = this.manifest.filter((value) => {
      return value.id === id
    })[0]

    if (item && item.geometry) {
      return item.geometry
    }
    return null
  }

  getMaterials(id) {
    var item = this.manifest.filter((value) => {
      return value.id === id
    })[0]

    if (item && item.materials) {
      return item.materials
    }
    return null
  }

  getTexture(id) {
    var item = this.manifest.filter((value) => {
      return value.id === id
    })[0]

    if (item && item.texture) {
      return item.texture
    }

    return null
  }

  getAudio(id) {
    var item = this.manifest.filter((value) => {
      return value.id === id
    })[0]

    if (item && item.buffer) {
      return item.buffer
    }

    return null
  }

  getRepeatedTexture(id, repeatX, repeatY) {
    const texture = this.getTexture(id)
    texture.wrapS = texture.wrapT = RepeatWrapping
    texture.repeat.set(repeatX, repeatY)
    return texture
  }

  getImage(id) {
    var item = this.manifest.filter((value) => {
      return value.id === id
    })[0]

    if (item && item.image) {
      return item.image
    }
    return null
  }

  getImagePositions(id) {
    var item = this.manifest.filter((value) => {
      return value.id === id
    })[0]

    if (item && item.positions) {
      return item.positions
    }
    return null
  }
}

export default new Preloader()
